import React, { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { AuthShell, Field } from "@/pages/Login";
import { GoogleLogo, AppleLogo, EnvelopeSimple, LockKey, User as UserIcon, Gift } from "@phosphor-icons/react";
import { toast } from "sonner";

function formatErr(detail) {
  if (!detail) return "Something went wrong.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

export default function Register() {
  const { register } = useAuth();
  const { t, lang } = useI18n();
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [pw, setPw] = useState("");
  const [referralCode, setReferralCode] = useState(sp.get("ref") || "");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr(""); setLoading(true);
    try {
      // The custom AuthContext.register only takes (email,password,name) — call API directly with ref
      const payload = { email, password: pw, name };
      if (referralCode.trim()) payload.referral_code = referralCode.trim().toUpperCase();
      const api = (await import("@/lib/api")).default;
      const { data } = await api.post("/auth/register", payload);
      if (data?.token) localStorage.setItem("tf_token", data.token);
      window.location.href = "/profile?onboarding=1";
    } catch (e) {
      setErr(formatErr(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/auth/callback";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const comingSoon = (provider) => toast(t("auth.coming_soon", { provider }));

  return (
    <AuthShell title={t("auth.create_title")} subtitle={t("auth.create_sub")}>
      <div className="space-y-2">
        <button data-testid="google-register-btn" onClick={googleLogin} className="kinetic-btn w-full border border-[#262626] hover:border-white/40 bg-[#141414] px-4 py-3 flex items-center justify-center gap-3 text-sm font-semibold">
          <GoogleLogo weight="bold" size={18} /> {t("common.continue_with_google")}
        </button>
        <div>
          <button data-testid="apple-register-btn" onClick={() => comingSoon("Apple")} className="kinetic-btn w-full border border-[#262626] hover:border-white/40 bg-[#141414] px-3 py-2.5 flex items-center justify-center gap-2 text-sm font-semibold">
            <AppleLogo weight="fill" size={16} /> {t("common.continue_with_apple") || "Continue with Apple"}
          </button>
        </div>
      </div>
      <div className="my-5 flex items-center gap-3">
        <div className="h-px flex-1 bg-[#262626]" />
        <span className="label-overline">{t("common.or")}</span>
        <div className="h-px flex-1 bg-[#262626]" />
      </div>
      <form onSubmit={onSubmit} className="space-y-3">
        <Field icon={<UserIcon size={16} weight="duotone" />}>
          <input data-testid="register-name" required value={name} onChange={(e) => setName(e.target.value)} placeholder={t("auth.fullname")} className="bg-transparent flex-1 outline-none text-sm placeholder:text-zinc-500" />
        </Field>
        <Field icon={<EnvelopeSimple size={16} weight="duotone" />}>
          <input data-testid="register-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder={t("common.email")} className="bg-transparent flex-1 outline-none text-sm placeholder:text-zinc-500" />
        </Field>
        <Field icon={<LockKey size={16} weight="duotone" />}>
          <input data-testid="register-password" type="password" required minLength={6} value={pw} onChange={(e) => setPw(e.target.value)} placeholder={t("auth.password_hint")} className="bg-transparent flex-1 outline-none text-sm placeholder:text-zinc-500" />
        </Field>
        <Field icon={<Gift size={16} weight="duotone" />}>
          <input
            data-testid="register-referral-code"
            value={referralCode}
            onChange={(e) => setReferralCode(e.target.value)}
            placeholder={lang === "da" ? "Inviteret? Indtast kode (valgfri)" : "Invited? Enter code (optional)"}
            className="bg-transparent flex-1 outline-none text-sm placeholder:text-zinc-500 uppercase tracking-wider"
          />
        </Field>
        {err && <div data-testid="register-error" className="text-xs text-[#FF3B30] border border-[#FF3B30]/40 bg-[#FF3B30]/10 px-3 py-2">{err}</div>}
        <button data-testid="register-submit" disabled={loading} className="kinetic-btn w-full bg-[#007AFF] hover:bg-[#0a66d0] disabled:opacity-60 text-white px-4 py-3 font-bold uppercase tracking-widest text-sm">
          {loading ? t("auth.create_loading") : t("auth.create_action")}
        </button>
      </form>
      <div className="mt-6 text-xs text-zinc-400 text-center">
        {t("auth.have_account")} <Link data-testid="register-to-login" to="/login" className="text-[#007AFF] hover:underline">{t("common.login")}</Link>
      </div>
    </AuthShell>
  );
}
